import type { CustomerInput } from "@/lib/api/customers";
import { parseDay, toDay } from "@/lib/core/format";
import { LABEL_PRESETS, QUOTE_LABEL_PRESETS } from "@/lib/invoice/label-presets";
import type { LabelName } from "@/lib/invoice/labels";
import type { InvoiceFormInput } from "@/lib/invoice/schema";

type DefaultsCustomer = Partial<
  Pick<CustomerInput, "paymentTermsDays" | "currency" | "invoiceLanguage">
>;

export type InvoiceChanges = Partial<
  Pick<InvoiceFormInput, "currency" | "dueDate" | "dueDateInDays">
> &
  Partial<Record<LabelName, string>>;

/**
 * What picking `customer` changes on an invoice or template: currency, payment terms (the due
 * date on invoices, the default days on templates) and the label language. Only values that
 * differ are returned; an empty result means nothing to apply.
 */
export function customerInvoiceChanges(
  customer: DefaultsCustomer,
  values: InvoiceFormInput,
  mode: "invoice" | "template" | "quote",
): InvoiceChanges {
  const changes: InvoiceChanges = {};
  if (customer.currency && customer.currency !== values.currency) {
    changes.currency = customer.currency;
  }
  if (customer.paymentTermsDays) {
    const days = Number(customer.paymentTermsDays);
    if (mode === "template" && days !== values.dueDateInDays) changes.dueDateInDays = days;
    // A quote's date is its validity, not the customer's payment terms.
    if (mode === "invoice" && values.issueDate) {
      const due = parseDay(values.issueDate);
      due.setDate(due.getDate() + days);
      if (toDay(due) !== values.dueDate) changes.dueDate = toDay(due);
    }
  }
  if (customer.invoiceLanguage) {
    const preset: Record<string, string> = {
      ...LABEL_PRESETS[customer.invoiceLanguage],
      ...(mode === "quote" ? QUOTE_LABEL_PRESETS[customer.invoiceLanguage] : {}),
    };
    for (const [name, label] of Object.entries(preset)) {
      const key = name as LabelName;
      // An empty label hides that part of the invoice (e.g. the quantity column): keep it hidden.
      if (values[key] && values[key] !== label) changes[key] = label;
    }
  }
  return changes;
}
